/**
 * THE TOOLTIP, IN OUR OWN VOICE.
 *
 * 🚨 **ALEX, 2026-08-18:** *"the tooltips are readable but lack zero design and are basic ugly
 * tooltip images. I think you can use your claude design skills in this UI as well."*
 *
 * He is describing exactly what they were: **the browser's native `title=` attribute.** Every tip on
 * the board went through it - the foxfire counter, the deck and graveyard piles, the hero power's
 * rules text, a card's refusal reason, the emote labels. So they rendered in the operating system's
 * font at the operating system's size in the operating system's box, after the operating system's
 * one-second delay, on a screen where every other pixel is hand-drawn. On a hand-built dark western
 * folklore board, an OS tooltip is the one element that announces the game is a web page.
 *
 * ⭐ **THIS UPGRADES EVERY EXISTING TOOLTIP WITHOUT TOUCHING A SINGLE CALL SITE.** It intercepts
 * `title` on first hover or focus, MOVES it to `data-tip`, and removes the attribute - so the native
 * tooltip can never fire and ten `el.title = "..."` lines around the codebase keep working. Fixing
 * them one by one would have been ten edits and a permanent invitation to add an eleventh.
 *
 * ⚠️ **`title` IS REMOVED, SO THE ACCESSIBLE NAME HAS TO BE REPLACED, NOT DROPPED.** A `title` is
 * read by screen readers; deleting it silently would trade a visual defect for an accessibility one.
 * The text is written to `aria-label` when the element has no other accessible name.
 *
 * ⚠️ **NO ENTRY DELAY, A SHORT EXIT ONE.** The OS delay is why players never read these. Leaving on
 * a delay stops the tip flickering as the pointer crosses a row of cards.
 */

const TIP_ATTR = "data-tip";

/**
 * RICH TIPS: a builder per element, for the tips that deserve more than a paragraph.
 *
 * 🚨 **ALEX, 2026-08-18:** *"the tooltips can still be more clear, and better designed, potentially
 * with some colour and the emoji of the thing it is explaining in front of the line - for example
 * for foxfire amount it shows the orange square and number like the one on the top left of the card,
 * and then the sentence explainer."*
 *
 * ⭐ **SO A TIP ROW CARRIES THE BOARD'S OWN DEVICE, NOT A NEW ONE.** The orange cost square, the
 * attack and health badges and the realm swatch already exist and already mean something to the
 * player; drawing a second set of glyphs for the tooltip would be a second owner of the card's
 * visual vocabulary and would drift the first time one of them is restyled.
 *
 * ⚠️ A BUILDER, NOT A NODE. Storing a live node would put one element per card in memory forever and
 * would break the moment two tips wanted the same node. The builder runs when the tip opens.
 */
const RICH = new WeakMap<Element, () => Node>();

/** Give `el` a rich tip. The plain `title`/`data-tip` text stays as the accessible fallback. */
export function setRichTip(el: Element, build: () => Node): void {
  RICH.set(el, build);
}

let tipEl: HTMLElement | null = null;
let hideTimer: number | null = null;
let current: Element | null = null;

function ensureEl(): HTMLElement {
  if (tipEl) return tipEl;
  const el = document.createElement("div");
  el.className = "tip";
  el.setAttribute("role", "tooltip");
  el.hidden = true;
  document.body.append(el);
  tipEl = el;
  return el;
}

/**
 * Take the native `title` off an element the first time we see it, so the OS tooltip is never armed.
 *
 * ⚠️ Returns the text either way, because an element may already have been converted.
 */
function textFor(el: Element): string {
  const existing = el.getAttribute(TIP_ATTR);
  if (existing) return existing;

  const title = el.getAttribute("title");
  if (!title) return "";

  el.setAttribute(TIP_ATTR, title);
  el.removeAttribute("title");
  // Preserve the accessible name that `title` was providing, unless one already exists.
  if (!el.getAttribute("aria-label") && !el.getAttribute("aria-labelledby")) {
    el.setAttribute("aria-label", title);
  }
  return title;
}

/** The nearest ancestor (or self) carrying a tip. Delegation, so dynamically drawn cards work. */
function tipTarget(from: EventTarget | null): Element | null {
  let n = from instanceof Element ? from : null;
  while (n && n !== document.body) {
    if (n.hasAttribute(TIP_ATTR) || n.hasAttribute("title") || RICH.has(n)) return n;
    n = n.parentElement;
  }
  return null;
}

function place(el: HTMLElement, anchor: Element): void {
  const a = anchor.getBoundingClientRect();
  el.hidden = false;
  const t = el.getBoundingClientRect();
  const pad = 10;

  // Prefer above; flip below when there is no room. A tip that runs off the top of the window is a
  // tip nobody reads, and the board's most-hovered controls sit near the top edge.
  let top = a.top - t.height - pad;
  if (top < pad) top = a.bottom + pad;

  // Centre on the anchor, then clamp inside the viewport so it can never be half off-screen.
  let left = a.left + a.width / 2 - t.width / 2;
  left = Math.max(pad, Math.min(left, window.innerWidth - t.width - pad));

  el.style.top = `${Math.round(top)}px`;
  el.style.left = `${Math.round(left)}px`;
}

function show(anchor: Element): void {
  const build = RICH.get(anchor);
  const text = textFor(anchor);
  if (!text && !build) return;
  if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }

  const el = ensureEl();
  if (build) {
    // ⚠️ Rebuilt each time: a card's stats change during a match, and a cached node would show the
    // creature as it was when it was first inspected.
    el.replaceChildren(build());
    el.classList.add("tip--rich");
  } else {
    el.classList.remove("tip--rich");
    // ⚠️ `textContent`, never `innerHTML`. A card's refusal string and a player's own name can both
    // reach here, and a tooltip is not a place to start rendering markup from data.
    el.textContent = text;
  }
  el.classList.add("tip--in");
  current = anchor;
  place(el, anchor);
}

function hide(): void {
  current = null;
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = window.setTimeout(() => {
    if (!tipEl) return;
    tipEl.classList.remove("tip--in");
    tipEl.hidden = true;
  }, 90);
}

/**
 * Arm the tooltip layer. Idempotent, and safe to call before any board exists: everything is
 * delegated off `document`, so cards drawn later are covered without re-arming.
 */
export function installTips(): void {
  if ((window as unknown as { __revivalTips?: boolean }).__revivalTips) return;
  (window as unknown as { __revivalTips?: boolean }).__revivalTips = true;

  /*
   * 🚨 **RIGHT-CLICK, NOT HOVER.** Alex, 2026-08-18: *"i think for tooltips and enlarge it should not
   * be scroll over, the user should right click the mouse - that will make it less busy and annoying
   * when you do not want the tooltip or zoom in."*
   *
   * ⛔ **HOVER MADE THE TIP AN INVOLUNTARY EVENT.** Every pointer journey across the board crossed
   * the foxfire rail, both decks, both graveyards and up to fifteen cards, and each crossing opened
   * something. The information was never the problem; the trigger was.
   *
   * ⚠️ **`preventDefault` ONLY ON AN ELEMENT THAT ACTUALLY HAS A TIP.** Killing the browser context
   * menu everywhere would remove copy, inspect and back from the whole app for the sake of a
   * tooltip. Right-clicking anywhere else behaves exactly as it always did.
   *
   * ⚠️ Cards handle their own right-click (`ui/cardZoom.ts`) and stop propagation, so a card shows
   * its full face rather than a text tip on top of it.
   */
  document.addEventListener("contextmenu", (e) => {
    const t = tipTarget(e.target);
    if (!t) { if (current) hide(); return; }
    e.preventDefault();
    if (t === current) { hide(); return; }   // right-click again to dismiss
    show(t);
  });

  /* 🔴 **MOVING TIPS TO RIGHT-CLICK BROUGHT THE BROWSER'S NATIVE TOOLTIP BACK.**
   *
   * The whole reason this layer exists is that `title=` renders in the OS font, in the OS box, after
   * the OS delay - Alex, 2026-08-18: *"the tooltips are readable but lack zero design and are basic
   * ugly tooltip images."* It was killed by CONVERTING `title` to `data-tip` on hover and removing
   * the attribute.
   *
   * ⛔ **THAT CONVERSION LIVED IN THE HOVER HANDLER.** When hover stopped opening tips, it stopped
   * converting too - so every `title` on the board went back to being a live native tooltip, and the
   * first thing Alex saw was the grey OS box we had already removed once. **A cleanup that rides on
   * a feature dies with the feature.**
   *
   * ⭐ So disarming is now its own job, independent of showing: hovering converts and removes the
   * attribute and displays NOTHING. Right-click still opens ours. */
  document.addEventListener("pointerover", (e) => {
    const t = tipTarget(e.target);
    if (t) textFor(t);          // moves title -> data-tip, so the OS tooltip can never fire
  }, { passive: true });

  // Any left click anywhere dismisses it - a tip opened deliberately still has to be easy to lose.
  document.addEventListener("pointerdown", (e) => {
    if ((e as PointerEvent).button === 0 && current) hide();
  }, { passive: true });

  // Keyboard parity: a control reachable by Tab must be readable without a pointer.
  document.addEventListener("focusin", (e) => {
    const t = tipTarget(e.target);
    if (t) show(t);
  });
  /* 🔴 **THIS WAS `() => hide()` AND IT KILLED RIGHT-CLICK TOOLTIPS THE DAY THEY SHIPPED.**
     Alex: *"the right click to view tooltips is not working."*

     Measured rather than guessed - the tip WAS being built correctly and then hidden:

         after right-click: tipExists true, tipRich true, tipHidden TRUE, display none
         content: "Golden Harvest Cider / 3 Foxfire to play / 2 4 attack and health / Confused..."

     ⛔ **A SELECTABLE CARD IS A `<button>`, SO RIGHT-CLICKING IT MOVES FOCUS.** That fires `focusout`
     on whatever had focus, and this listener hid the tip a moment after `contextmenu` opened it.
     Harmless while tips opened on HOVER, because a hover tip was already gone by then; fatal the
     moment opening became a CLICK. **The feature that broke it is not the feature that changed.**

     ⭐ It now mirrors the old `pointerout` rule: only the element that OWNS the tip can dismiss it by
     losing focus. */
  document.addEventListener("focusout", (e) => {
    if (tipTarget(e.target) === current) hide();
  });

  // ⛔ A tip must never survive the thing it describes. Scrolling, resizing or pressing Escape all
  // mean "the anchor may have moved or gone", and a stale tooltip floating over a new board is
  // worse than none.
  window.addEventListener("scroll", () => { if (current) hide(); }, { passive: true, capture: true });
  window.addEventListener("resize", () => { if (current) hide(); }, { passive: true });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape" && current) hide(); });
}
